import type { PdfField as AchPdfField } from "../types";
import type { PdfFieldRect } from "@/section/pdf/types";

export interface PdfFieldMetaShape extends Record<string, unknown> {
  label?: string;
  value?: string;
  fontSize?: number;
  ach?: {
    fieldKey?: string;
    fieldType?: AchPdfField["field_type"];
    systemFieldType?: AchPdfField["system_field_type"];
    fieldDestType?: AchPdfField["field_dest_type"];
    isRequired?: boolean;
    isReadonly?: boolean;
  };
}

type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

function normalizePage(page?: number | null) {
  if (typeof page === "number" && Number.isFinite(page) && page > 0) {
    return Math.floor(page);
  }
  return 1;
}

function normalizeRectCoordinates(field: AchPdfField): Mutable<PdfFieldRect["rect"]> {
  const { position_data: data } = field;
  return {
    x: Number(data?.x ?? 0),
    y: Number(data?.y ?? 0),
    width: Number(data?.width ?? 0),
    height: Number(data?.height ?? 0),
  };
}

function toMeta(field: AchPdfField): PdfFieldMetaShape {
  return {
    label: field.field_key ?? field.field_id,
    value: field.value ?? field.freeform_value ?? "",
    fontSize: field.position_data?.text_size,
    ach: {
      fieldKey: field.field_key,
      fieldType: field.field_type,
      systemFieldType: field.system_field_type,
      fieldDestType: field.field_dest_type,
      isRequired: field.is_required,
      isReadonly: field.is_readonly,
    },
  };
}

export function achFieldToPdfFieldRect(field: AchPdfField): PdfFieldRect {
  return {
    id: field.field_id,
    page: normalizePage(field.position_data?.page),
    rect: normalizeRectCoordinates(field),
    meta: toMeta(field),
  };
}

export function achFieldsToPdfFieldRects(fields: AchPdfField[]): PdfFieldRect[] {
  return fields.map(achFieldToPdfFieldRect);
}

function resolveAchMeta(rect: PdfFieldRect): PdfFieldMetaShape["ach"] {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return meta?.ach ?? {};
}

function resolveValue(rect: PdfFieldRect): string | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.value === "string" ? meta.value : undefined;
}

function resolveFontSize(rect: PdfFieldRect): number | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.fontSize === "number" ? meta.fontSize : undefined;
}

export function pdfFieldRectToAchField(rect: PdfFieldRect, base?: Partial<AchPdfField>): AchPdfField {
  const achMeta = resolveAchMeta(rect);
  const value = resolveValue(rect);
  const page = normalizePage(rect.page);

  return {
    field_id: base?.field_id ?? rect.id,
    field_key: base?.field_key ?? achMeta?.fieldKey ?? rect.id,
    field_type: base?.field_type ?? achMeta?.fieldType ?? "system",
    system_field_type: base?.system_field_type ?? achMeta?.systemFieldType,
    field_dest_type: base?.field_dest_type ?? achMeta?.fieldDestType,
    position_data: {
      x: rect.rect.x,
      y: rect.rect.y,
      width: rect.rect.width,
      height: rect.rect.height,
      text_size: resolveFontSize(rect) ?? base?.position_data?.text_size,
      page,
    },
    is_required: base?.is_required ?? achMeta?.isRequired,
    is_readonly: base?.is_readonly ?? achMeta?.isReadonly,
    freeform_value: base?.freeform_value ?? value,
    value: base?.value ?? value,
  };
}

export function pdfFieldRectsToAchFields(rects: PdfFieldRect[], existing: Record<string, AchPdfField> = {}): AchPdfField[] {
  return rects.map((rect) => pdfFieldRectToAchField(rect, existing[rect.id]));
}




